/**
 * Allowlist for a rich text content block's HTML. Mirrors the content block editor's toolbar
 * exactly (headings H1-H3, bold/italic/underline/strike, lists, plain links) — the same set
 * FormDescriptionEditor already exposes for the form-level description. No `style` attribute is
 * allowed: the editor's toolbar never produces inline styles, so there's nothing to allowlist.
 */

/** Tags allowed in sanitized output. Used by both the server-side sanitizer
 *  (rich-text-sanitize.ts, sanitize-html at write time) and the client-side renderer
 *  (ContentBlockPreview.tsx, DOMPurify at render time) — keep the two in sync, and in sync
 *  with useSimpleRichTextEditor's enabled heading levels. */
export const RICH_TEXT_ALLOWED_TAGS = [
  "h1",
  "h2",
  "h3",
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "a",
  "ul",
  "ol",
  "li",
] as const;

/** Attributes allowed on an `<a>` tag. Shared by both sanitize passes like {@link RICH_TEXT_ALLOWED_TAGS} —
 *  note the server scopes these to `a` only (sanitize-html's tag-scoped `allowedAttributes`), while
 *  DOMPurify's flatter `ALLOWED_ATTR` technically permits them on any allowed tag; low practical risk
 *  since DOMPurify's own URI-scheme guard still applies regardless of which tag carries `href`. */
export const RICH_TEXT_ALLOWED_ATTR = ["href", "target", "rel"] as const;

/** Allowed link URI schemes. Server-only (sanitize-html's `allowedSchemes`) — DOMPurify applies its
 *  own built-in scheme allowlist on the client pass rather than reading this constant. */
export const RICH_TEXT_ALLOWED_SCHEMES = ["http", "https", "mailto"] as const;

/** Character cap on a single content block's stored HTML — cheap insurance against runaway
 *  JSONB row growth from a feature that invites much longer content than a question label. */
export const RICH_TEXT_MAX_LENGTH = 20_000;
